import { useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/components/checkout/buttonStyles";
import { fmtLong } from "./timelineUtils";

/** Confirmation for a drag-and-drop move. `move` = { kind, seg, fromRoom, toRoom, newArrival, newDeparture }. */
export function MoveDialog({ move, onConfirm, onCancel, busy }) {
  const [reason, setReason] = useState("");
  if (!move) return null;
  const { kind, seg, fromRoom, toRoom, newArrival, newDeparture } = move;
  const roomChanged = fromRoom.room_id !== toRoom.room_id;
  const datesChanged = kind === "reservation" && (newArrival !== seg.check_in || newDeparture !== seg.check_out);
  const who = seg.guest_name || (kind === "stay" ? "Guest" : "Upcoming reservation");
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="bg-white border border-[#D5E6E8] md:max-w-[440px]" data-testid="timeline-move-dialog">
        <DialogHeader>
          <DialogTitle className="text-[16px] text-[#1A2425]">{kind === "stay" ? "Move guest to another room?" : "Move reservation?"}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-[#3F5257]">{who}{seg.reservation_code ? ` · ${seg.reservation_code}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-[13px]">
          <div className="flex items-center gap-2 rounded-md bg-[#F4FCFC] border border-[#E6EFF0] px-3 py-2" data-testid="move-rooms">
            <span className={roomChanged ? "text-[#3F5257]" : "text-[#1A2425] font-semibold"}>Room {fromRoom.room_number}</span>
            {roomChanged && <><ArrowRight size={14} strokeWidth={1.5} className="text-[#014751]" /><span className="font-bold text-[#014751]">Room {toRoom.room_number}</span><span className="text-[11px] text-[#97A6A9]">· {toRoom.room_type}</span></>}
          </div>
          {kind === "reservation" && (
            <div className="flex items-center gap-2 rounded-md bg-[#F4FCFC] border border-[#E6EFF0] px-3 py-2" data-testid="move-dates">
              <span className={datesChanged ? "text-[#3F5257]" : "text-[#1A2425] font-semibold"}>{fmtLong(seg.check_in)} → {fmtLong(seg.check_out)}</span>
              {datesChanged && <><ArrowRight size={14} strokeWidth={1.5} className="text-[#014751]" /><span className="font-bold text-[#014751]">{fmtLong(newArrival)} → {fmtLong(newDeparture)}</span></>}
            </div>
          )}
          {kind === "stay" && <p className="text-[11.5px] text-[#97A6A9]">The guest is checked in — only the room changes; dates stay as they are.</p>}
          <Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-9 text-[13px] border-[#D5E6E8]" data-testid="move-reason" />
        </div>
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button type="button" className={BTN_SECONDARY} onClick={onCancel} disabled={busy} data-testid="move-cancel">Cancel</button>
          <button type="button" className={BTN_PRIMARY} onClick={() => onConfirm(reason)} disabled={busy || (!roomChanged && !datesChanged)} data-testid="move-confirm">{busy && <Loader2 size={14} className="animate-spin" />}Confirm Move</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
